import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { MODULES, ROLE_LABELS } from "@/lib/constants";
import InactivityWatcher from "./InactivityWatcher";
import ErrorBoundary from "./ErrorBoundary";
import {
  LayoutDashboard,
  Users,
  ShieldAlert,
  LogOut,
  Instagram,
  Facebook,
  Music2,
  MessageCircle,
  Mail,
  Sparkles,
  Calendar,
  Menu,
  X,
  PlugZap,
  Building2,
  KeyRound,
  CalendarRange,
  Zap,
  FileText,
  ShoppingBag,
  Home,
  MessageSquare,
  Newspaper,
  Inbox,
  UsersRound,
  Receipt,
  Activity,
  Youtube,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { useCompany } from "@/context/CompanyContext";
import NotificationBell from "@/components/NotificationBell";
import { Avatar as ProfileAvatar } from "@/pages/Perfil";

const ICON_MAP = {
  Instagram,
  Facebook,
  Music2,
  MessageCircle,
  Mail,
  Sparkles,
  ShoppingBag,
  Home,
  MessageSquare,
  Newspaper,
  Youtube,
};

function Brand() {
  return (
    <div className="flex items-center gap-3 px-6 py-6" data-testid="sidebar-brand">
      <img src="/assets/logo.png" alt="FStudy Manager" className="h-11 w-11 object-contain" />
      <div>
        <div className="font-serif-display text-lg font-semibold leading-none text-brand-ink">
          FStudy <span className="text-primary">Manager</span>
        </div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mt-1">
          Create ART, Design & Marketing
        </div>
      </div>
    </div>
  );
}

function NavItem({ to, icon: Icon, label, testid, end = false }) {
  return (
    <NavLink
      to={to}
      end={end}
      data-testid={testid}
      className={({ isActive }) =>
        [
          "group flex items-center gap-3 px-6 py-2.5 text-sm font-medium transition-all duration-200 border-l-[3px]",
          isActive
            ? "bg-brand-emerald-soft text-brand-emerald-dark border-brand-gold"
            : "text-stone-600 border-transparent hover:bg-stone-50 hover:text-brand-ink",
        ].join(" ")
      }
    >
      <Icon className="h-4 w-4 shrink-0" strokeWidth={1.8} />
      <span>{label}</span>
    </NavLink>
  );
}

function SectionLabel({ children }) {
  return (
    <div className="px-6 mt-6 mb-2 text-[10px] uppercase tracking-[0.18em] text-stone-400 font-semibold">
      {children}
    </div>
  );
}

export default function DashboardLayout() {
  const { user, logout, isMaster, hasModule } = useAuth();
  const { companies, active, activeId, setActiveId } = useCompany();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const initials = (user?.name || "?")
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  // Módulos habilitados basados en la EMPRESA ACTIVA (fase 6).
  const effectiveModuleActive = (key) => {
    if (active) return Boolean(active.effective_modules?.[key]);
    return hasModule(key);
  };
  const enabledModules = MODULES.filter((m) => effectiveModuleActive(m.key));

  // Fase 6: acceso a calendario multi-empresa (master siempre, admin con lista autorizada)
  const canSeeCrossCompany =
    isMaster || (user?.role === "admin" && (user?.global_calendar_companies || []).length > 0);

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const SidebarInner = (
    <>
      <Brand />
      <div className="flex-1 overflow-y-auto pb-6">
        <SectionLabel>General</SectionLabel>
        <NavItem
          to="/dashboard"
          end
          icon={LayoutDashboard}
          label="Panel Principal"
          testid="nav-dashboard"
        />
        <NavItem
          to="/calendario"
          icon={Calendar}
          label="Calendario"
          testid="nav-calendar"
        />

        {canSeeCrossCompany && (
          <NavItem
            to="/calendario-multi"
            icon={CalendarRange}
            label="Calendario Multi-Empresa"
            testid="nav-cross-company-calendar"
          />
        )}

        {enabledModules.length > 0 && (
          <>
            <SectionLabel>Módulos</SectionLabel>
            {enabledModules.map((m) => {
              const Icon = ICON_MAP[m.icon] || Sparkles;
              return (
                <NavItem
                  key={m.key}
                  to={m.path}
                  icon={Icon}
                  label={m.label}
                  testid={`nav-module-${m.key}`}
                />
              );
            })}
          </>
        )}

        {isMaster && (
          <>
            <SectionLabel>Configuración Maestra</SectionLabel>
            <NavItem
              to="/diagnostico"
              icon={Activity}
              label="Estado del Sistema"
              testid="nav-diagnostics"
            />
            <NavItem
              to="/dashboard-cross"
              icon={LayoutDashboard}
              label="Dashboard Cross-Empresa"
              testid="nav-dashboard-cross"
            />
            <NavItem
              to="/conectividad"
              icon={PlugZap}
              label="Conectividad"
              testid="nav-connectivity"
            />
            <NavItem
              to="/integraciones"
              icon={KeyRound}
              label="Integraciones"
              testid="nav-integrations"
            />
            <NavItem
              to="/empresas"
              icon={Building2}
              label="Empresas"
              testid="nav-companies"
            />
            <NavItem
              to="/equipo"
              icon={Users}
              label="Gestión de Equipo"
              testid="nav-team"
            />
            {(user?.email || "").toLowerCase() === "fabiolicep@gmail.com" && (
              <NavItem
                to="/configuracion/landing"
                icon={Home}
                label="Editor de Landing"
                testid="nav-landing-editor"
              />
            )}

            <SectionLabel>Administración</SectionLabel>
            <NavItem
              to="/consumo-ia"
              icon={Zap}
              label="Consumo IA"
              testid="nav-consumo-ia"
            />
            <NavItem
              to="/reportes"
              icon={FileText}
              label="Reportes"
              testid="nav-reportes"
            />
            <NavItem
              to="/facturacion"
              icon={Receipt}
              label="Facturación"
              testid="nav-billing"
            />
            <NavItem
              to="/auditoria"
              icon={ShieldAlert}
              label="Auditoría"
              testid="nav-audit"
            />
            <NavItem
              to="/interesados"
              icon={Inbox}
              label="Interesados"
              testid="nav-interesados"
            />
            {effectiveModuleActive("clientes") && (
              <NavItem
                to="/clientes"
                icon={UsersRound}
                label="Agenda FStudy"
                testid="nav-clientes"
              />
            )}
          </>
        )}
        {!isMaster && user?.role === "admin" && (
          <>
            <SectionLabel>Administración</SectionLabel>
            <NavItem
              to="/equipo-empresa"
              icon={Users}
              label="Gestión de Equipo"
              testid="nav-team-company"
            />
            {effectiveModuleActive("clientes") && (
              <NavItem
                to="/clientes"
                icon={UsersRound}
                label="Agenda FStudy"
                testid="nav-clientes"
              />
            )}
            <NavItem
              to="/reportes"
              icon={FileText}
              label="Reportes"
              testid="nav-reportes"
            />
            <NavItem
              to="/facturacion"
              icon={Receipt}
              label="Facturación"
              testid="nav-billing-admin"
            />
          </>
        )}
      </div>

      <div className="border-t border-border px-4 py-4 bg-stone-50/60">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate("/perfil")}
            data-testid="sidebar-avatar-button"
            title="Mi perfil"
            className="shrink-0"
          >
            <ProfileAvatar user={user} size={40} className="border border-brand-gold/60" />
          </button>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-brand-ink truncate" data-testid="sidebar-user-name">
              {user?.name}
            </div>
            <div className="text-xs text-muted-foreground truncate" data-testid="sidebar-user-role">
              {user?.title || ROLE_LABELS[user?.role] || user?.role}
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleLogout}
            data-testid="logout-button"
            className="text-stone-500 hover:text-brand-emerald hover:bg-brand-emerald-soft"
            title="Cerrar sesión"
          >
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Fase 13 · Auto-logout por inactividad de 7 min con aviso al minuto 6. */}
      <InactivityWatcher />
      {/* Sidebar desktop */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 z-30 w-72 flex-col bg-white border-r border-border shadow-soft">
        {SidebarInner}
      </aside>

      {/* Sidebar mobile */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-40">
          <div
            className="absolute inset-0 bg-brand-ink/40"
            onClick={() => setMobileOpen(false)}
            data-testid="mobile-sidebar-backdrop"
          />
          <aside
            className="absolute inset-y-0 left-0 w-72 bg-white flex flex-col border-r border-border animate-fade-in-up"
            onClick={(e) => {
              // Cierra al hacer clic en un enlace de navegación (link o button de navegación)
              const el = e.target.closest("a, button");
              if (el && el.getAttribute("data-testid")?.startsWith("nav-")) {
                setMobileOpen(false);
              }
            }}
          >
            {SidebarInner}
          </aside>
        </div>
      )}

      {/* Header + content */}
      <div className="lg:pl-72">
        <header className="sticky top-0 z-20 bg-white/85 backdrop-blur-xl border-b border-border">
          <div className="flex items-center justify-between px-4 sm:px-6 lg:px-10 h-16 gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden shrink-0"
                onClick={() => setMobileOpen((v) => !v)}
                data-testid="mobile-menu-toggle"
                aria-label={mobileOpen ? "Cerrar menú" : "Abrir menú"}
              >
                {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </Button>
              <div className="min-w-0">
                <div className="text-[10px] sm:text-xs uppercase tracking-[0.18em] text-stone-400 truncate">
                  FStudy Manager
                </div>
                <div className="font-serif-display text-base sm:text-lg font-medium text-brand-ink -mt-0.5 truncate">
                  Bienvenido, {user?.name?.split(" ")[0]}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-3 shrink-0">
              {companies.length > 0 && (
                <div className="w-[140px] sm:w-[200px] md:w-[220px]" data-testid="company-selector">
                  <Select value={activeId || ""} onValueChange={(v) => { setActiveId(v); window.location.reload(); }}>
                    <SelectTrigger className="h-9 bg-white text-xs sm:text-sm">
                      <SelectValue placeholder="Seleccionar empresa" />
                    </SelectTrigger>
                    <SelectContent>
                      {companies.map((c) => (
                        <SelectItem key={c.id} value={c.id} data-testid={`company-option-${c.id}`}>
                          <span className="inline-flex items-center gap-2">
                            <span className="h-5 w-5 rounded flex items-center justify-center text-white text-[10px] font-bold" style={{ backgroundColor: c.color || "#00A36C" }}>
                              {c.initial || c.name?.[0]}
                            </span>
                            {c.name}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="hidden lg:flex items-center gap-2 text-xs text-stone-500">
                <span className="inline-flex h-2 w-2 rounded-full bg-brand-emerald animate-pulse" />
                Sesión activa
              </div>
              <NotificationBell />
            </div>
          </div>
        </header>

        <main className="px-4 sm:px-6 lg:px-10 py-6 sm:py-8 lg:py-10 max-w-[1400px] mx-auto">
          <ErrorBoundary section="esta sección">
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>
    </div>
  );
}
